<template>
    <component :is="asTab ? 'b-tab' : 'div'" lazy title-item-class="mb-1">
        <template v-if="asTab" slot="title">
            KeyboardShortcuts
        </template>

        <b-form-group
            label="Keyboard Shortcuts"
            label-size="lg"
            label-class="font-weight-bold pt-0">
            <b-overlay :show="isLoading()" rounded="sm" variant="light" :opacity="0.80" blur="2px">
                <b-row>
                    <b-col>
                        <b-card no-body>
                            <template v-slot:header>
                                <span class="align-middle">Available Shortcuts</span>
                                <div class="card-header-actions">
                                    <b-button size="sm" variant="primary" @click="showCreateModal">
                                        <i class="fa-solid fa-plus"></i> New Shortcut
                                    </b-button>
                                </div>
                            </template>

                            <b-list-group flush>
                                <b-list-group-item
                                    class="p-2 flex-column align-items-start"
                                    v-for="(shortcut, idx) in available"
                                    :key="`avail-${shortcut.id}-${idx}`">
                                    <div class="d-flex w-100 justify-content-between">
                                        <UpdateShortcut :item="shortcut" />
                                        <b-link @click="activateShortcut(idx)"><i class="fa-solid fa-plus"></i></b-link>
                                    </div>
                                </b-list-group-item>
                                <b-list-group-item class="text-center" v-if="!available.length && !isLoading()">
                                    <i class="fa-solid fa-ban fa-xl"></i>
                                </b-list-group-item>
                            </b-list-group>
                        </b-card>
                    </b-col>

                    <b-col>
                        <b-card no-body>
                            <template slot="header">
                                <span class="align-middle">Activated Shortcuts</span>
                            </template>

                            <b-list-group flush>
                                <b-list-group-item
                                    class="p-2 flex-column align-items-start"
                                    v-for="(shortcut, idx) in activated"
                                    :key="`active-${shortcut.id}-${idx}`">
                                    <div class="d-flex w-100 justify-content-between">
                                        <UpdateShortcut :item="shortcut" />
                                        <b-link @click="deactivateShortcut(idx)"><i class="fa-solid fa-minus"></i></b-link>
                                    </div>
                                </b-list-group-item>
                                <b-list-group-item class="text-center" v-if="!activated.length && !isLoading()">
                                    Add shortcuts to activate them
                                </b-list-group-item>
                            </b-list-group>
                        </b-card>
                    </b-col>
                </b-row>
            </b-overlay>

            <b-alert v-if="error" show variant="danger" class="my-2">
                <small>{{ error }}</small>
            </b-alert>
        </b-form-group>

        <b-modal
            v-model="showCreateModalRef"
            title="New Shortcut"
            centered
            @ok="saveNewShortcut"
            @shown="focusShortcode"
            :ok-disabled="saving || !canSaveNew"
            ok-title="Save">
            <b-form @submit.prevent="saveNewShortcut">
                <b-form-group label="Shortcode" label-for="new_keys">
                    <b-form-input id="new_keys" ref="createShortcutInput" v-model.trim="newShortcut.keys" :disabled="saving" />
                    <small class="text-muted">Reserved: <span v-for="(r, i) in reservedShortcodes" :key="r">@{{ r }}<span v-if="i < reservedShortcodes.length - 1">, </span></span></small>
                </b-form-group>
                <b-form-group label="Description" label-for="new_description">
                    <b-form-input id="new_description" v-model.trim="newShortcut.description" :disabled="saving" />
                </b-form-group>
                <b-form-group>
                    <b-form-checkbox v-model="newShortcut.is_public" :disabled="saving">
                        Public
                    </b-form-checkbox>
                </b-form-group>
                <b-alert v-if="createError" show variant="danger" class="mt-2">
                    <small>{{ createError }}</small>
                </b-alert>
            </b-form>
        </b-modal>
    </component>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import UpdateShortcut from './UpdateShortcut'

export default {
    name: 'keyboard-shortcuts-manager',
    components: {
        UpdateShortcut
    },
    props: {
        asTab: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            newShortcut: {
                keys: "",
                description: "",
                section: "other",
                is_public: false
            },
            saving: false,
            error: null,
            createError: null,
            showCreateModalRef: false,
            available: [],
            activated: [],
            reservedShortcodes: ['pr']
        }
    },
    computed: {
        ...mapGetters('keyboardshortcuts', ['getAllShortcuts', 'isLoading']),
        shortcuts() {
            return this.getAllShortcuts()
        },
        currentUserId() {
            return this.$store.state.auth?.current?.id || null
        },
        newKeysRaw() {
            return (this.newShortcut.keys || '').replace(/^@/, '').trim()
        },
        canSaveNew() {
            return this.newKeysRaw && this.newShortcut.description.trim() && !this.reservedShortcodes.includes(this.newKeysRaw.toLowerCase())
        }
    },
    methods: {
        ...mapActions('keyboardshortcuts', ['fetchShortcuts', 'addShortcut', 'deleteShortcut', 'updateShortcut']),
        populateLists() {
            const all = Array.from(this.shortcuts || [])
            // Only show shortcuts owned by the logged-in user
            const mine = this.currentUserId
                ? all.filter(s => s.user && s.user.id === this.currentUserId)
                : all
            this.activated = mine.filter(s => s.is_active)
            this.available = mine.filter(s => !s.is_active)
        },
        showCreateModal() {
            this.resetCreateForm()
            this.showCreateModalRef = true
        },
        focusShortcode() {
            this.$refs.createShortcutInput?.focus()
        },
        resetCreateForm() {
            this.newShortcut = { keys: "", description: "", section: "other", is_public: false }
            this.createError = null
        },
        async saveNewShortcut(evt) {
            if (!this.canSaveNew) {
                evt?.preventDefault()
                return
            }

            this.saving = true
            this.createError = null

            try {
                // normalize keys (allow user to type with or without leading @)
                const keysRaw = (this.newShortcut.keys || '').replace(/^@/, '').trim()
                if (!keysRaw) {
                    this.createError = 'Shortcut keys cannot be empty'
                    evt?.preventDefault()
                    return
                }

                if (this.reservedShortcodes.includes(keysRaw.toLowerCase())) {
                    this.createError = 'This shortcode is reserved and cannot be used'
                    evt?.preventDefault()
                    return
                }

                await this.addShortcut({
                    keys: keysRaw,
                    description: this.newShortcut.description,
                    is_active: true,
                    is_public: this.newShortcut.is_public
                })

                this.$root.$emit('shortcut-added', {
                    keys: this.newShortcut.keys,
                    description: this.newShortcut.description
                })

                this.resetCreateForm()
            } catch (err) {
                this.createError = err.response?.data?.keys?.[0] || 'Failed to save shortcut'
                evt?.preventDefault()
                return
            } finally {
                this.saving = false
            }

            this.showCreateModalRef = false
        },
        activateShortcut(idx) {
            const item = this.available[idx]
            if (!item) return

            this.updateShortcut({ id: item.id, data: { is_active: true } })
                .then((updated) => {
                    this.available = this.available.filter((_, i) => i !== idx)
                    this.activated.push(updated)
                })
                .catch(() => {
                    this.error = 'Failed to activate shortcut'
                })
        },
        deactivateShortcut(idx) {
            const item = this.activated[idx]
            if (!item) return

            this.updateShortcut({ id: item.id, data: { is_active: false } })
                .then((updated) => {
                    this.activated = this.activated.filter((_, i) => i !== idx)
                    this.available.push(updated)
                })
                .catch(() => {
                    this.error = 'Failed to deactivate shortcut'
                })
        }
    },
    async mounted() {
        try {
            await this.fetchShortcuts()
            this.populateLists()
        } catch (err) {
            this.error = 'Failed to load shortcuts'
        }
    },
    watch: {
        shortcuts: {
            immediate: true,
            handler() {
                this.populateLists()
            }
        }
    }
}
</script>
